import React, { useContext } from 'react';
import Divider from '@material-ui/core/Divider';
import NestedMenuItem from 'material-ui-nested-menu-item';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import EditorContext from '@mirinae/lessons/editor/modules/contexts/editor';
import { useMenuClasses, useMenuItemClasses } from '@mirinae/lessons/editor/components/Navigator/TreeContextMenu';
import { colorChoices } from '@mirinae/lessons/editor/modules/defines/style';
import deepClone from "deep-clone";

const SpecContextMenu = React.forwardRef((props, ref) => {
    const { spec, onClose, updateSpec, setSRObject } = props;
    const editor = useContext(EditorContext);
    const menuClasses = useMenuClasses();
    const menuItemClasses = useMenuItemClasses();

    const moveSection = (move) => {
        const { section, page } = editor.specContextMenu.context;
        spec.sections.splice(section.index, 1);
        spec.sections.splice(section.index + move, 0, section);
        spec.sections.forEach((s, i) => s.index = i);
        updateSpec();
    };

    const duplicateSection = () => {
        const { section } = editor.specContextMenu.context;
        spec.sections.splice(section.index + 1, 0, { ...deepClone(section), title: `${section.title} copy` });
        spec.sections.forEach((s, i) => s.index = i);
        updateSpec();
    };

    const insertSection = (direction) => {
        const { section } = editor.specContextMenu.context;
        spec.sections.splice(section.index + (direction < 0 ? 0 : 1), 0, { title: '-- new section --', pages: [{ title: '-- new page --', content: '', index: 0 }] });
        spec.sections.forEach((s, i) => s.index = i);
        updateSpec();
    };

    const deleteSection = () => {
        const { section } = editor.specContextMenu.context;
        spec.sections.splice(section.index, 1);
        spec.sections.forEach((s, i) => s.index = i);
        updateSpec();
    };

     const searchReplaceInPage = () => {
        const { page } = editor.specContextMenu.context;
        setSRObject(page);
    };

    const movePage = (move) => {
        const { section, page } = editor.specContextMenu.context;
        section.pages.splice(page.index, 1);
        section.pages.splice(page.index + move, 0, page);
        section.pages.forEach((p, i) => p.index = i);
        updateSpec();
    };

    const duplicatePage = () => {
        const { section, page } = editor.specContextMenu.context;
        section.pages.splice(page.index + 1, 0, { ...deepClone(page), title: `${page.title} copy` });
        section.pages.forEach((p, i) => p.index = i);
        updateSpec();
    };

    const insertPage = (direction) => {
        const { section, page } = editor.specContextMenu.context;
        section.pages.splice(page.index + (direction < 0 ? 0 : 1), 0, { title: '-- new page --', content: '' });
        section.pages.forEach((p, i) => p.index = i);
        updateSpec();
    };

    const deletePage = () => {
        const { section, page } = editor.specContextMenu.context;
        section.pages.splice(page.index, 1);
        section.pages.forEach((p, i) => p.index = i);
        updateSpec();
    };

    const addAnimSpec = () => {
        const { page } = editor.specContextMenu.context;
        page.elements.push({ type: 'patternAnimation', animation: [['--step--', '--pattern--', '--bubbles--' ]] });
        updateSpec();
    };

    const pasteAnimSpec = () => {
        const { page } = editor.specContextMenu.context;
        if (spec.copiedAnim) {
            const clone = deepClone(spec.copiedAnim);
            clone.id = undefined;
            page.elements.push(clone);
            updateSpec();
        }
    };

    const moveStep = (move) => {
        const { animation, step, stepIndex } = editor.specContextMenu.context;
        animation.splice(stepIndex, 1);
        animation.splice(stepIndex + move, 0, step);
        updateSpec();
    };

    const duplicateStep = () => {
        const { animation, step, stepIndex } = editor.specContextMenu.context;
        animation.splice(stepIndex + 1, 0, deepClone(step));
        updateSpec();
    };

    const insertStep = (direction) => {
        const { animation, step, stepIndex } = editor.specContextMenu.context;
        animation.splice(stepIndex + (direction < 0 ? 0 : 1), 0, ['--step--', '--pattern--', '--bubbles--' ]);
        updateSpec();
    };

    const deleteStep = () => {
        const { animation, step, stepIndex } = editor.specContextMenu.context;
        animation.splice(stepIndex, 1);
        updateSpec();
    };

    const duplicateAnimation = () => {
        const { animPage, animID } = editor.specContextMenu.context;
        const index = animPage.elements.findIndex((e, i) => e.id == animID);
        if (index >= 0) {
            const clone = deepClone(animPage.elements[index]);
            clone.id = undefined;
            animPage.elements.splice(index, 0, clone);
            updateSpec();
        }
    };

    const copyAnimation = () => {
        const { animPage, animID } = editor.specContextMenu.context;
        const index = animPage.elements.findIndex((e, i) => e.id == animID);
        if (index >= 0)
            spec.copiedAnim = deepClone(animPage.elements[index]);
    };

    const deleteAnimation = () => {
        const { animPage, animID } = editor.specContextMenu.context;
        const index = animPage.elements.findIndex((e, i) => e.id == animID);
        if (index >= 0) {
            animPage.elements.splice(index, 1);
            updateSpec();
        }
    };

     const searchReplaceInAnim = () => {
        const { animPage, animID } = editor.specContextMenu.context;
        const anim = animPage.elements.find(e => e.id == animID);
        if (anim) {
            setSRObject(anim);
        }
    };

    const menuItems = () => {
        const { section, page, animation, stepIndex } = editor.specContextMenu.context;
        let items = [];
        if (page) {
            items = items.concat([
                { label: 'Search & replace in page', handler: searchReplaceInPage },
                { divider: true },
                { label: 'Insert page above', handler: () => insertPage(-1) },
                { label: 'Insert page below', handler: () => insertPage(1) },
                { label: 'Move page up', disable: () => page.index === 0, handler: () => movePage(-1) },
                { label: 'Move page down',  disable: () => page.index >= section.pages.length - 1, handler: () => movePage(1) },
                { label: 'Duplicate page', handler: duplicatePage },
                { divider: true },
                { label: 'Add animation spec to page', handler: addAnimSpec },
                { label: 'Paste animation spec', disable: () => !spec.copiedAnim, handler: pasteAnimSpec },
                { divider: true },
                { label: 'Delete page', handler: deletePage },
            ])
        } else if (section) {
            items = items.concat([
                { label: 'Insert section above', handler: () => insertSection(-1) },
                { label: 'Insert section below', handler: () => insertSection(1) },
                { label: 'Move section up', disable: () => section.index === 0, handler: () => moveSection(-1) },
                { label: 'Move section down',  disable: () => section.index >= spec.sections.length - 1, handler: () => moveSection(1) },
                { label: 'Duplicate section', handler: duplicateSection },
                { divider: true },
                { label: 'Delete section', handler: deleteSection },
            ])
        } else if (animation) {
             items = items.concat([
                { label: 'Search & replace in animation', handler: searchReplaceInAnim },
                { divider: true },
                { label: 'Insert step above', handler: () => insertStep(-1) },
                { label: 'Insert step below', handler: () => insertStep(1) },
                { label: 'Move step up', disable: () => stepIndex === 0, handler: () => moveStep(-1) },
                { label: 'Move step down',  disable: () => stepIndex >= animation.length - 1, handler: () => moveStep(1) },
                { label: 'Duplicate step', handler: duplicateStep },
                { divider: true },
                { label: 'Delete step', handler: deleteStep },
                { divider: true },
                { label: 'Copy animation', handler: copyAnimation },
                { label: 'Duplicate animation', handler: duplicateAnimation },
                { divider: true },
                { label: 'Delete animation', handler: deleteAnimation },
            ])
        }

        return items;
    };

    const MenuEntry = React.forwardRef((props, ref) => {
        const { item, open } = props;
        return (
            (item.divider || item.label === '-' ? (
                <Divider />
            ) : (item.submenu ? (
                <NestedMenuItem classes={menuItemClasses} label={item.label} parentMenuOpen={open}>
                    {item.submenu.map((subitem, i) => <MenuEntry key={i} item={subitem} open={open} />)}
                </NestedMenuItem>
            ) : (
                <MenuItem
                    classes={menuItemClasses}
                    disabled={item.disable ? item.disable(item) : false}
                    selected={item.selected && item.selected(editData())}
                    onClick={(e) => {
                        item.handler(item, e);
                        onClose();
                        editor.update({ specContextMenu: { open: false, context: {} } });
                    }}
                >
                    {typeof item.label === 'function' ? item.label(item) : item.label}
                </MenuItem>
            )))
        );
    });

    return (
        <Menu
            ref={ref}
            classes={menuClasses}
            open={editor.specContextMenu.open}
            onClose={() => { onClose(); editor.update({ specContextMenu: { open: false, context: {} } }); }}
            anchorReference="anchorPosition"
            anchorPosition={{ top: editor.specContextMenu.context.mouseY + 10, left: editor.specContextMenu.context.mouseX - 30 }}
            // transformOrigin={{ vertical: -20, 'horizontal': -10 }}
        >
            { menuItems().map((item, i) => <MenuEntry key={i} item={item} open={!!editor.specContextMenu.open} />)}
        </Menu>
    );
});

export default SpecContextMenu;